import { useTranslation } from "react-i18next";
import { ColorDot } from "~/components/charts";
import { Text } from "~/components/text";
import {
  formatBucketDate,
  formatCurrency,
  formatNumber,
} from "~/utils/formatting";
import { cn } from "~/utils";
import type { SalesChartDatum } from "~/pages/product-details/tabs/sales-data-tab/data/map-sales-response";

export interface TotalSalesTooltipSeries {
  key: string;
  label: string;
  currency: string;
  color: string;
  colorClassName?: string;
}

export interface TotalSalesTooltipProps {
  series: TotalSalesTooltipSeries[];
  display: "combined" | "byMarket";
  active?: boolean;
  payload?: { payload: SalesChartDatum }[];
}

const Swatch = ({ entry }: { entry: TotalSalesTooltipSeries }) => (
  <ColorDot
    className={entry.colorClassName}
    style={entry.colorClassName ? undefined : { backgroundColor: entry.color }}
    ring
  />
);

export const TotalSalesTooltip = ({
  series,
  display,
  active,
  payload,
}: TotalSalesTooltipProps) => {
  const { t } = useTranslation(["productDetails"]);

  if (!active || !payload?.length) {
    return null;
  }
  const datum = payload[0].payload;
  const isByMarket = display === "byMarket";
  const detailByKey = datum.detail ?? {};
  const total = datum.total ?? 0;

  const unitsSuffix = t(
    "productDetails:salesData.totalSales.tooltip.unitsSuffix",
  );

  const revenueText = (entry: TotalSalesTooltipSeries): string | null => {
    const detail = detailByKey[entry.key];
    if (detail?.revenue == null) {
      return null;
    }
    return formatCurrency(detail.revenue, entry.currency);
  };

  const combinedRevenueLine = isByMarket
    ? ""
    : [
        ...series
          .reduce((byCurrency, entry) => {
            const detail = detailByKey[entry.key];
            if (detail?.revenue != null) {
              byCurrency.set(
                entry.currency,
                (byCurrency.get(entry.currency) ?? 0) + detail.revenue,
              );
            }
            return byCurrency;
          }, new Map<string, number>())
          .entries(),
      ]
        .map(([currency, revenue]) => formatCurrency(revenue, currency))
        .join(" · ");

  return (
    <div
      className={cn(
        "grid min-w-[11rem] gap-1.5 rounded-lg bg-secondary-800 px-3 py-2 text-xs text-white shadow-xl",
      )}
    >
      <Text as="div" variant="tooltip">
        {formatBucketDate(datum.date)}
      </Text>

      {isByMarket ? (
        <>
          <div className="flex justify-between gap-4 font-semibold">
            <Text as="span" variant="tooltip">
              {t("productDetails:salesData.totalSales.tooltip.units")}
            </Text>
            <Text as="span" variant="tooltip" numeric="mono">
              {formatNumber(total)} {unitsSuffix}
            </Text>
          </div>
          <div className="grid gap-1">
            {series.map((entry) => {
              const detail = detailByKey[entry.key];
              if (!detail?.units) {
                return null;
              }
              return (
                <div
                  key={entry.key}
                  className="flex items-center justify-between gap-4"
                >
                  <Text
                    as="span"
                    variant="tooltip"
                    className="flex items-center gap-1.5"
                  >
                    <Swatch entry={entry} />
                    {entry.label}
                  </Text>
                  <Text as="span" variant="tooltip" numeric="mono">
                    {formatNumber(detail.units)} {unitsSuffix}
                  </Text>
                </div>
              );
            })}
          </div>

          <div className="grid gap-1 border-t border-white/10 pt-1.5">
            {/* Same variant as Units; colour muted for hierarchy. */}
            <Text as="span" variant="tooltip" className="text-white/50">
              {t("productDetails:salesData.totalSales.tooltip.salesByMarket")}
            </Text>
            {series.map((entry) => {
              const revenue = revenueText(entry);
              if (!revenue) {
                return null;
              }
              return (
                <div
                  key={entry.key}
                  className="flex items-center justify-between gap-4"
                >
                  <Text
                    as="span"
                    variant="tooltip"
                    className="flex items-center gap-1.5"
                  >
                    <Swatch entry={entry} />
                    {entry.label}
                  </Text>
                  <Text as="span" variant="tooltip" numeric="mono">
                    {revenue}
                  </Text>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <>
          <div className="flex justify-between gap-4">
            <Text as="span" variant="tooltip">
              {t("productDetails:salesData.totalSales.tooltip.units")}
            </Text>
            <Text as="span" variant="tooltip" numeric="mono">
              {formatNumber(total)} {unitsSuffix}
            </Text>
          </div>
          {combinedRevenueLine && (
            <div className="flex justify-between gap-4 border-t border-white/10 pt-1.5">
              <Text as="span" variant="tooltip">
                {t("productDetails:salesData.totalSales.tooltip.sales")}
              </Text>
              <Text as="span" variant="tooltip" numeric="mono">
                {combinedRevenueLine}
              </Text>
            </div>
          )}
        </>
      )}
    </div>
  );
};
